import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Clock, X } from "lucide-react";
import { api, shopToday } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { deliverFile } from "@/lib/pdfFiling";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "Last 7 days" },
  { key: "month", label: "Last 30 days" },
  { key: "quarter", label: "Last quarter" },
  { key: "year", label: "Last year" },
  { key: "custom", label: "Custom range" },
];

const mins = (v) => (v ? `${v} min` : "-");

const SOURCE_LABELS = {
  self: "QR self-scan",
  staff: "Staff-added",
  unknown: "Not recorded",
};
const sourceLabel = (s) => SOURCE_LABELS[s] || SOURCE_LABELS.unknown;

export default function QueueWaits() {
  const { branch } = useAuth();
  const [period, setPeriod] = useState("today");
  const [fromDate, setFromDate] = useState(shopToday());
  const [toDate, setToDate] = useState(shopToday());
  const [allBranches, setAllBranches] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  const params = useCallback(
    () => ({
      period,
      ...(period === "custom" ? { from_date: fromDate, to_date: toDate } : {}),
      ...(allBranches ? {} : { branch }),
    }),
    [period, fromDate, toDate, allBranches, branch]
  );

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/reports/queue-waits", { params: params() })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error("Could not load the waiting times"))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(load, [load]);

  const download = async () => {
    try {
      const res = await api.get("/reports/queue-waits.csv", {
        params: params(),
        responseType: "blob",
      });
      await deliverFile(
        res.data,
        "Reports",
        `dhunna-queue-waits-${shopToday()}.csv`,
        toast
      );
    } catch {
      toast.error("Could not build the spreadsheet");
    }
  };

  const openDay = async (date) => {
    try {
      const { data: d } = await api.get("/reports/queue-waits/day", {
        params: { date, ...(allBranches ? {} : { branch }) },
      });
      setDrill(d);
    } catch {
      toast.error("Could not load that day's list");
    }
  };

  const t = data?.totals;
  const busiest = data?.by_hour?.length
    ? [...data.by_hour].sort((a, b) => b.customers - a.customers).slice(0, 6)
    : [];

  return (
    <div className="fade-up space-y-7" data-testid="queue-waits-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
            {allBranches ? "BOTH STORES" : branch}
          </p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Queue Waiting Times</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            How long customers wait before being called, and when the counter is busiest.
          </p>
        </div>
        <Button
          onClick={download}
          disabled={!data?.rows?.length}
          variant="outline"
          className="h-11 rounded-none border-[#C5A059] px-6 font-semibold"
          data-testid="queue-waits-csv-button"
        >
          <Download className="mr-2 h-4 w-4" /> Download spreadsheet
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 border border-border p-5">
        <div className="flex flex-wrap gap-px bg-border">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              data-testid={`queue-waits-period-${p.key}`}
              className={`px-4 py-2 text-sm transition-colors duration-150 ${
                period === p.key
                  ? "bg-[#0A0A0A] font-semibold text-white"
                  : "bg-white hover:bg-[#FBF8F2]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                From
              </Label>
              <Input
                type="date"
                className="h-10 w-[170px] rounded-none"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="queue-waits-from"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                To
              </Label>
              <Input
                type="date"
                className="h-10 w-[170px] rounded-none"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="queue-waits-to"
              />
            </div>
          </>
        )}
        <label className="flex cursor-pointer items-center gap-2 pb-2.5 text-sm">
          <input
            type="checkbox"
            checked={allBranches}
            onChange={(e) => setAllBranches(e.target.checked)}
            className="accent-[#C5A059]"
            data-testid="queue-waits-all-branches"
          />
          Include both stores
        </label>
      </div>

      <div className="grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2 xl:grid-cols-5">
        {[
          ["Customers", String(t?.customers ?? 0), "customers"],
          ["Average per day", String(t?.avg_per_day ?? 0), "per-day"],
          ["Average wait", mins(t?.avg_wait), "avg-wait"],
          ["Longest wait", mins(t?.longest_wait), "longest"],
          ["Busiest hour", t?.busiest_hour || "-", "busiest"],
        ].map(([label, value, key]) => (
          <div key={key} className="bg-white p-5" data-testid={`queue-waits-stat-${key}`}>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {label}
            </div>
            <div className="tabular mt-3 text-2xl font-bold">{value}</div>
          </div>
        ))}
      </div>

      {busiest.length > 0 && (
        <div className="border border-border p-5" data-testid="queue-waits-hours">
          <h2 className="text-base font-bold uppercase tracking-wider md:text-lg">
            Busiest hours of the day
          </h2>
          <div className="mt-4 space-y-2">
            {busiest.map((h) => (
              <div key={h.hour} className="flex items-center gap-3 text-sm">
                <span className="tabular w-16 font-semibold">{h.hour}</span>
                <div className="h-4 flex-1 bg-[#F4F1EA]">
                  <div
                    className="h-4 bg-[#C5A059]"
                    style={{
                      width: `${Math.min(
                        100,
                        (h.customers / (busiest[0].customers || 1)) * 100
                      )}%`,
                    }}
                  />
                </div>
                <span className="tabular w-28 text-right text-muted-foreground">
                  {h.customers} in · {mins(h.avg_wait)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data?.by_reason?.length > 0 || data?.by_staff?.length > 0) && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {data?.by_reason?.length > 0 && (
            <div className="border border-border p-5" data-testid="queue-waits-by-reason">
              <h2 className="text-base font-bold uppercase tracking-wider md:text-lg">
                Why customers came
              </h2>
              <div className="mt-4 space-y-2">
                {data.by_reason.map((r) => (
                  <div key={r.reason} className="flex items-center gap-3 text-sm">
                    <span className="w-28 text-xs font-semibold uppercase tracking-wider">
                      {r.reason}
                    </span>
                    <div className="h-4 flex-1 bg-[#F4F1EA]">
                      <div
                        className="h-4 bg-[#C5A059]"
                        style={{
                          width: `${Math.min(100, (r.customers / (data.by_reason[0].customers || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                    <span className="tabular w-10 text-right font-semibold">{r.customers}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data?.by_staff?.length > 0 && (
            <div className="border border-border p-5" data-testid="queue-waits-by-staff">
              <h2 className="text-base font-bold uppercase tracking-wider md:text-lg">
                Served by
              </h2>
              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-1.5">Staff</th>
                    <th className="py-1.5 text-right">Served</th>
                    <th className="py-1.5 text-right">No shows</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.by_staff.map((s) => (
                    <tr key={s.staff} data-testid={`queue-staff-${s.staff}`}>
                      <td className="py-1.5 font-semibold">{s.staff}</td>
                      <td className="tabular py-1.5 text-right">{s.served}</td>
                      <td className="tabular py-1.5 text-right">{s.no_shows || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {data?.by_source?.length > 0 && (
        <div className="border border-border p-5" data-testid="queue-waits-by-source">
          <h2 className="text-base font-bold uppercase tracking-wider md:text-lg">
            How they joined
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Customers who scanned the counter QR code themselves vs tickets staff added by hand.
          </p>
          <div className="mt-4 space-y-2">
            {data.by_source.map((s) => (
              <div key={s.source} className="flex items-center gap-3 text-sm">
                <span className="w-32 text-xs font-semibold uppercase tracking-wider">
                  {sourceLabel(s.source)}
                </span>
                <div className="h-4 flex-1 bg-[#F4F1EA]">
                  <div
                    className="h-4 bg-[#C5A059]"
                    style={{
                      width: `${Math.min(100, (s.customers / (data.by_source[0].customers || 1)) * 100)}%`,
                    }}
                  />
                </div>
                <span className="tabular w-10 text-right font-semibold">{s.customers}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto border border-border">
        <table className="w-full min-w-[860px] text-sm" data-testid="queue-waits-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              {[
                "Date",
                "Day",
                "Customers",
                "Average wait",
                "Longest wait",
                "Average time served",
                "No shows",
                "Busiest hour",
              ].map((h) => (
                <th key={h} className="px-3 py-2.5 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {!loading && !data?.rows?.length && (
              <tr>
                <td colSpan={8} className="px-3 py-12 text-center text-muted-foreground">
                  <Clock className="mx-auto mb-2 h-5 w-5 text-[#C5A059]" />
                  Nobody has used the queue in this period yet.
                </td>
              </tr>
            )}
            {(data?.rows || []).map((r) => (
              <tr key={r.date}
                  onClick={() => openDay(r.date)}
                  className="cursor-pointer hover:bg-[#FBF8F2]"
                  data-testid={`queue-waits-row-${r.date}`}>
                <td className="tabular px-3 py-2.5 font-semibold text-[#B38F4E] underline">{r.date}</td>
                <td className="px-3 py-2.5">{r.weekday}</td>
                <td className="tabular px-3 py-2.5 font-semibold">{r.customers}</td>
                <td className="tabular px-3 py-2.5">{mins(r.avg_wait)}</td>
                <td className="tabular px-3 py-2.5">{mins(r.longest_wait)}</td>
                <td className="tabular px-3 py-2.5">{mins(r.avg_service)}</td>
                <td className="tabular px-3 py-2.5">{r.no_shows || "-"}</td>
                <td className="tabular px-3 py-2.5">{r.busiest_hour}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drill && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
             data-testid="queue-day-dialog">
          <div className="my-8 w-full max-w-3xl border border-border bg-white p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-bold md:text-lg">Who came — {drill.date}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {drill.count} ticket(s) · {drill.branch}
                </p>
              </div>
              <button type="button" onClick={() => setDrill(null)} data-testid="queue-day-close"
                      aria-label="Close">
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="mt-4 max-h-[60vh] overflow-y-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-[#0A0A0A] text-white">
                  <tr>
                    {["No.", "Name", "Here for", "Joined via", "Joined", "Wait", "Status", "Served by"].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {drill.rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                        Nobody came through the queue on this day.
                      </td>
                    </tr>
                  )}
                  {drill.rows.map((r, i) => (
                    <tr key={i} data-testid={`queue-day-row-${r.ticket}`}>
                      <td className="tabular px-3 py-2 font-bold">{r.ticket}</td>
                      <td className="px-3 py-2">
                        {r.phone ? (
                          <button
                            type="button"
                            onClick={() => window.open(`/customers/${encodeURIComponent(r.phone)}`, "_blank", "noopener")}
                            className="text-left font-medium text-[#8A6C36] underline-offset-2 hover:underline"
                            title="Open customer record"
                            data-testid={`queue-day-customer-link-${r.ticket}`}
                          >
                            {r.name}
                          </button>
                        ) : (
                          r.name
                        )}
                        {(r.party_size || 1) > 1 && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            party of {r.party_size}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">
                        {r.reason}
                      </td>
                      <td className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">
                        {sourceLabel(r.source)}
                      </td>
                      <td className="tabular px-3 py-2">{r.joined}</td>
                      <td className="tabular px-3 py-2">{r.wait != null ? `${r.wait} min` : "-"}</td>
                      <td className="px-3 py-2 text-xs uppercase tracking-wider">
                        {(r.status || "").replace("_", " ")}
                      </td>
                      <td className="px-3 py-2">{r.served_by || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
